import Icon from "@components/Icon";
import Text from "@components/Text";
import { styled } from "@linaria/react";
import { useAvivaDispatch, useAvivaSelector } from "@redux/hook";
import { updateOpenChildItem } from "@redux/slices/SHIELD_STARTER/step3Slice";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ITheme } from "src/theme";

const Container = styled.div`
  display: block;
  font-family: "Open Sans";
`;
const HeaderContainer = styled.div<StyleProps>`
  position: relative;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.18s linear;
  padding: ${(props) =>
    props.headerPadding ? props.headerPadding : "24px 24px"};
  background-color: ${(props) =>
    props.showHeaderBackground
      ? props.headerBackgroundColor
        ? props.headerBackgroundColor
        : "#FFF"
      : "#FFF"};
  border-radius: ${(props) => (props.isOpen ? "16px 16px 0 0" : "16px")};
  /* @media screen and (max-width: 768px) {
       border-radius: 0; 
  } */
`;
const BottomContainer = styled.div<StyleProps>`
  position: relative;
  margin-left: ${(props) =>
    props.contentMarginLeft ? props.contentMarginLeft : "0"};
  margin-right: ${(props) =>
    props.contentMarginRight ? props.contentMarginRight : "0"};
  background: #fff;
  border-radius: 0 0 16px 16px;
`;
const TitleContainer = styled.div`
  flex: 1;
  font-weight: 600;
  font-size: 18px;
`;
const ExtraElementContainer = styled.div`
  flex: 0;
`;
const IconContainer = styled.div<StyleProps>`
  display: flex;
  align-items: center;
  flex: 0;
  margin-right: 15px;
`;

const ContentContainer = styled.div<StyleProps>`
  height: ${(props) => (props.isOpen ? props.height + "" : "0px")};
  overflow: hidden;
  transition: height 0.18s linear;
`;
const HiddenContainer = styled.div<StyleProps>`
  height: ${(props) => (props.isOpen ? "auto" : "0px")};
  display: block;
  transition: height 0.18s linear;
`;
const UnderlineContainer = styled.div<StyleProps>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) =>
    props.bottomLineColor ? props.bottomLineColor : "#000"};
  height: ${(props) =>
    props.bottomLineThickness ? props.bottomLineThickness : "0px"};
`;
const HeaderUnderlineContainer = styled.div<StyleProps>`
  border-bottom: 1px solid
    ${(props) => (props.bottomLineColor ? props.bottomLineColor : "#000")};
  height: ${(props) =>
    props.bottomLineThickness ? props.bottomLineThickness : "0px"};
  margin-left: ${(props) =>
    props.contentMarginLeft ? props.contentMarginLeft : "0"};
  margin-right: ${(props) =>
    props.contentMarginRight ? props.contentMarginRight : "0"};
`;

interface StyleProps {
  height?: string;
  isOpen?: boolean;
  showHeaderBackground?: boolean;
  headerBackgroundColor?: string;
  showBottomLine?: boolean;
  bottomLineThickness?: string;
  bottomLineColor?: string;
  bottomLineClassName?: string;
  contentMarginLeft?: string;
  contentMarginRight?: string;
  showHeaderLine?: boolean;
  headerPadding?: string;
  headerMargin?: string;
}

export interface AccordionProps {
  className?: string | undefined;
  id?: string | undefined;
  theme?: ITheme;
  title?: string | undefined;
  titleClassName?: string | undefined;
  titleFontSize?: number;
  children: React.ReactNode;
  titleColor?: string | undefined;
  headerPadding?: string | undefined;
  headerMargin?: string | undefined;
  showHeaderBackground?: boolean | undefined;
  headerBackgroundColor?: string | undefined;
  contentMarginLeft?: string;
  contentMarginRight?: string;
  showBottomLine?: boolean | undefined;
  showHeaderLine?: boolean | undefined;
  bottomLineClassName?: string | undefined;
  bottomLineColor?: string | undefined;
  bottomLineThickness?: string | undefined;
  isExpanded: boolean;
  showExtraElement?: boolean | undefined;
  extraElement?: JSX.Element | undefined;
  item?: any;
}

const Accordion = forwardRef(
  (
    {
      children,
      className,
      title,
      titleClassName,
      titleFontSize,
      titleColor,
      showHeaderBackground = false,
      showHeaderLine = false,
      headerBackgroundColor,
      contentMarginLeft,
      contentMarginRight,
      showBottomLine = false,
      bottomLineColor,
      bottomLineThickness,
      bottomLineClassName,
      showExtraElement = false,
      extraElement,
      isExpanded = false,
      headerPadding,
      headerMargin,
      item,
      id
    }: AccordionProps,
    ref
  ) => {
    const dispatch = useAvivaDispatch();
    const openIcon = "carat-down",
      closeIcon = "carat-right";
    const [isOpen, setOpen] = useState<boolean>(isExpanded);
    const [icon, setIcon] = useState<string>(isExpanded ? openIcon : closeIcon);
    const [contentHeight, setContentHeight] = useState<string>("auto");
    const contentRef = useRef<HTMLDivElement>(null);
    const openChildItem = useAvivaSelector(state=>state.shieldStarterStep3.openChildItem)

    const clickProcess = useCallback((titleActive: string) => {
        setOpen((previous) => {
          let value = !previous;
          setIcon(value ? openIcon : closeIcon);
          return value;
        });
        dispatch(updateOpenChildItem(titleActive));
    }, []);

    useImperativeHandle(ref, () => ({
      closeAccordion() {
        setOpen((previous) => {
          let value = false;
          setIcon(value ? openIcon : closeIcon);
          return value;
        });
      },
      openAccordion() {
        setOpen((previous) => {
          let value = true;
          setIcon(value ? openIcon : closeIcon);
          return value;
        });
      },
    }));

    useEffect(() => {
      if (contentRef.current?.clientHeight) {
        setContentHeight(contentRef.current?.clientHeight + "px");
      }
      const handleResize = () => {
        setContentHeight(contentRef.current?.clientHeight + "px");
      };
      window.addEventListener("resize", handleResize);
      
      setOpen(isExpanded);
      setIcon(isExpanded ? openIcon : closeIcon);
      
    }, [contentHeight, contentRef, isExpanded, openChildItem]);

    return (
      <Container className={className}>
        {showHeaderLine && (
          <HeaderUnderlineContainer
            className={bottomLineClassName}
            bottomLineThickness={bottomLineThickness}
            bottomLineColor={bottomLineColor}
            contentMarginLeft={contentMarginLeft}
            contentMarginRight={contentMarginRight}
          />
        )}
        <HeaderContainer
          className="headerForChildInfo"
          isOpen={isOpen}
          showHeaderBackground={showHeaderBackground}
          headerBackgroundColor={headerBackgroundColor}
          onClick={()=>clickProcess(id || "")}
          contentMarginLeft={contentMarginLeft}
          contentMarginRight={contentMarginRight}
          headerPadding={headerPadding}
          headerMargin={headerMargin}
        >
          <IconContainer isOpen={isOpen}>
            <Icon name={icon} size="md" />
          </IconContainer>
          <TitleContainer>
            <Text
              className={titleClassName}
              fontWeight="600"
              color={titleColor ? titleColor : "#434343"}
              fontSize={titleFontSize ? titleFontSize : 18}
            >
              {title}
            </Text>
          </TitleContainer>
          {showExtraElement && (
            <ExtraElementContainer>{extraElement}</ExtraElementContainer>
          )}
        </HeaderContainer>

        <BottomContainer
          contentMarginLeft={contentMarginLeft}
          contentMarginRight={contentMarginRight}
        >
          <ContentContainer
            isOpen={isOpen}
            height={contentHeight}
            showBottomLine={showBottomLine}
            bottomLineColor={bottomLineColor}
          >
            <HiddenContainer isOpen={isOpen} ref={contentRef}>
              {children}
            </HiddenContainer>
          </ContentContainer>
          {showBottomLine && (
            <UnderlineContainer
              className={bottomLineClassName}
              bottomLineThickness={bottomLineThickness}
              bottomLineColor={bottomLineColor}
            />
          )}
        </BottomContainer>
      </Container>
    );
  }
);
export default Accordion;
